import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ObjectQueryFilter } from '@tj/tj-dmp-lib-api';

export type ManualAcquisitionScope = 'PERSONAL' | 'COMMAND';

export interface ManualAcquisitionPreviewRow {
  /** Dataview main-row id; used for deselectedMainRowIds when the user unchecks a row */
  mainRowId: number;
  personalId: number;
  commandId?: number | null;
  email?: string | null;
  nameLabel?: string | null;
  currentAmount?: number | null;
  campaignCostContact?: number | null;
  campaignCostOrder?: number | null;
  eligible: boolean;
  blockReason?: string | null;
}

export interface ManualAcquisitionPreviewResponse {
  rows: ManualAcquisitionPreviewRow[];
  totalElements: number;
  eligibleInPage: number;
  ineligibleInPage: number;
}

export interface ManualAcquisitionBulkJobStatus {
  jobId: string;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';
  totalRows: number;
  processedRows: number;
  updatedCount: number;
  skippedCount: number;
  /** Rows without usable contact id (PERSONAL) or command id (COMMAND), before rules. */
  skippedMissingTarget?: number;
  skippedAwaitingTermination?: number;
  skippedImportCampaign?: number;
  skippedNotSelected?: number;
  skippedOtherIneligible?: number;
  skippedError?: number;
  errorMessage?: string | null;
}

@Injectable({ providedIn: 'root' })
export class ManualAcquisitionCostService {
  private readonly baseUrl = environment.SERVER_API_URL + 'services/engine/api/costs/manual-acquisition';

  constructor(private http: HttpClient) {}

  preview(
    tenant: string,
    partner: string,
    dataviewId: string,
    page: number,
    size: number,
    scope: ManualAcquisitionScope,
    queryFilter: ObjectQueryFilter
  ): Observable<ManualAcquisitionPreviewResponse> {
    let headers = new HttpHeaders().set('X-DataStoreID', tenant).set('X-PARTNER-ID', partner);
    return this.http.post<ManualAcquisitionPreviewResponse>(
      `${this.baseUrl}/preview/dataview/${encodeURIComponent(dataviewId)}?page=${page}&size=${size}&scope=${scope}`,
      queryFilter || {},
      { headers }
    );
  }

  startBulkApplyByFilter(
    tenant: string,
    partner: string,
    dataviewId: string,
    scope: ManualAcquisitionScope,
    amount: number,
    queryFilter: ObjectQueryFilter,
    /** Main-row ids the user unchecked among éligible rows; full filter is still scanned server-side. */
    deselectedMainRowIds: number[]
  ): Observable<{ jobId: string }> {
    const headers = new HttpHeaders().set('X-DataStoreID', tenant).set('X-PARTNER-ID', partner);
    return this.http.post<{ jobId: string }>(
      `${this.baseUrl}/bulk-apply/dataview/${encodeURIComponent(dataviewId)}?scope=${scope}`,
      { amount, queryFilter: queryFilter || {}, deselectedMainRowIds },
      { headers }
    );
  }

  getBulkJob(tenant: string, jobId: string): Observable<ManualAcquisitionBulkJobStatus> {
    return this.http.get<ManualAcquisitionBulkJobStatus>(
      `${this.baseUrl}/bulk-apply/jobs/${encodeURIComponent(jobId)}`,
      { headers: new HttpHeaders().set('X-DataStoreID', tenant) }
    );
  }
}
